import { resolveStreamUrl } from "../player/streamToken.js";
import type { ProbeResult } from "./channelHealthStore.js";

/**
 * The TV's own check of a hidden channel (native health/ChannelCheckPlugin): the box fetches the
 * first few kilobytes of the stream itself, one channel at a time. Inside the APK there is no
 * server to ask (the website's POST /api/health/probe never answered there), and the release
 * makes no calls of ours anyway.
 */
interface CapacitorLike {
  isNativePlatform?: () => boolean;
  nativePromise?: (plugin: string, method: string, options?: Record<string, unknown>) => Promise<unknown>;
  PluginHeaders?: Array<{ name: string }>;
}

const PLUGIN = "ChannelCheck";

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

/** True inside an APK that carries the check. */
export function channelCheckAvailable(): boolean {
  return capacitor() !== null;
}

let checksInFlight = 0;

/** How many checks are out right now — for the debug server's `busy`. */
export function channelChecksInFlight(): number {
  return checksInFlight;
}

/** Check one channel. `url` in the result is the catalog's own (redacted) URL, never the real one. */
export async function checkChannelOnDevice(streamUrl: string): Promise<ProbeResult> {
  const cap = capacitor();
  const checkedAt = () => new Date().toISOString();
  if (!cap) return { url: streamUrl, ok: false, status: null, checkedAt: checkedAt() };
  checksInFlight++;
  try {
    const { url } = await resolveStreamUrl(streamUrl);
    const answer = (await cap.nativePromise!(PLUGIN, "check", { url })) as { ok?: boolean; status?: number };
    const status = typeof answer?.status === "number" && answer.status > 0 ? answer.status : null;
    return { url: streamUrl, ok: answer?.ok === true, status, checkedAt: checkedAt() };
  } catch {
    return { url: streamUrl, ok: false, status: null, checkedAt: checkedAt() };
  } finally {
    checksInFlight--;
  }
}
